/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue';
import Router from 'vue-router';
import store from './store/store';
import auth from './services/authService';
import url from '@/services/urlService';

Vue.use(Router)

const router = new Router({
  mode: 'hash',
  base: process.env.VUE_APP_ROOT_DOMAIN,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          name: 'HomePage',
          component: () => import('@/views/static/HomePage.vue'),
          meta: {
            rule: 'guest'
          }
        },
      ]
    },

    {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/home'
        },
        {
          path: '/dashboard',
          name: 'dashboard',
          component: () => import('./views/Dashboard.vue'),
          meta: {
            rule: 'sales'
          }
        },

        // =============================================================================
        // Pages Routes
        // =============================================================================
        {
          path: '/pages/profile',
          name: 'page-profile',
          component: () => import('@/views/pages/profile-settings/UserSettings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'Profile', active: true }
            ],
            pageTitle: 'Profile',
            rule: 'sales'
          }
        },
        {
          path: '/orders',
          name: 'orders',
          component: () => import('@/views/pages/orders/Orders.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Orders', active: true }
            ],
            pageTitle: 'Orders',
            rule: 'sales'
          }
        },
        {
          path: '/orders/:order_id',
          name: 'orders',
          component: () => import('@/views/pages/orders/OrderEditor.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Orders', active: true }
            ],
            pageTitle: 'Orders',
            rule: 'sales'
          }
        },
        {
          path: '/forms',
          name: 'forms',
          component: () => import('@/views/pages/forms/Forms.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Forms', active: true }
            ],
            pageTitle: 'Forms',
            rule: 'sales'
          }
        },
        {
          path: '/forms/:form_id',
          name: 'forms',
          component: () => import('@/views/pages/forms/FormEditor.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Forms', active: true }
            ],
            pageTitle: 'Forms',
            rule: 'sales'
          }
        },
        {
          path: '/forms/new',
          name: 'forms',
          component: () => import('@/views/pages/forms/FormEditor.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Forms', active: true }
            ],
            pageTitle: 'Forms',
            rule: 'sales'
          }
        },
        {
          path: '/team',
          name: 'team-management',
          component: () => import('@/views/pages/team/Team.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Team Management', active: true }
            ],
            pageTitle: 'Team Management',
            rule: 'admin'
          }
        },
        {
          path: '/product',
          name: 'product',
          component: () => import('@/views/pages/product/Product.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Product', active: true }
            ],
            pageTitle: 'Products',
            rule: 'sales'
          }
        },
        {
          path: '/product/add',
          name: 'add-product',
          component: () => import('@/views/pages/product/AddProduct.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Add Product', active: true }
            ],
            pageTitle: 'Add Product',
            rule: 'sales'
          }
        },
        {
          path: '/product/edit',
          name: 'edit-product',
          component: () => import('@/views/pages/product/EditProduct.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Edit Product', active: true }
            ],
            pageTitle: 'Edit Product',
            rule: 'sales'
          }
        },
        {
          path: '/product/detail',
          name: 'product-details',
          component: () => import('@/views/pages/product/ProductDetails.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Product Details', active: true }
            ],
            pageTitle: 'Product Details',
            rule: 'sales'
          }
        },
        {
          path: '/products/inventory',
          name: 'products-inventory',
          component: () => import('@/views/pages/product/Inventory.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Inventory', active: true }
            ],
            pageTitle: 'Inventory',
            rule: 'sales'
          }
        },
        {
          path: '/coupons',
          name: 'coupons',
          component: () => import('@/views/pages/coupons/Coupons.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Coupons', active: true }
            ],
            pageTitle: 'Coupons',
            rule: 'sales'
          }
        },
        {
          path: '/profile-settings',
          name: 'profile-settings',
          component: () => import('@/views/pages/profile-settings/UserSettings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Profile Settings', active: true }
            ],
            pageTitle: 'Profile Settings',
            rule: 'sales'
          }
        },
        {
          path: '/store-settings',
          name: 'store-settings',
          component: () => import('@/views/pages/store-settings/StoreSettings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Store Settings', active: true }
            ],
            pageTitle: 'Store Settings',
            rule: 'admin'
          }
        },
        {
          path: '/support/faq',
          name: 'support-faq',
          component: () => import('@/views/pages/Faq.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'FAQ', active: true }
            ],
            pageTitle: 'FAQ',
            rule: 'sales'
          }
        },
        {
          path: '/support/contact-us',
          name: 'support-contact-us',
          component: () => import('@/views/pages/ContactUs.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Contact Us', active: true }
            ],
            pageTitle: 'Contact Us',
            rule: 'sales'
          }
        },
        {
          path: '/support/knowledge-base',
          name: 'knowledge-base',
          component: () => import('@/views/pages/knowledge-base/KnowledgeBase.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'KnowledgeBase', active: true }
            ],
            pageTitle: 'KnowledgeBase',
            rule: 'sales'
          }
        },
        {
          path: '/support/knowledge-base/category',
          name: 'knowledge-base-category',
          component: () => import('@/views/pages/knowledge-base/KnowledgeBaseCategory.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'KnowledgeBase', url: '/support/knowledge-base' },
              { title: 'Category', active: true }
            ],
            parent: 'knowledge-base',
            rule: 'sales'
          }
        },
        {
          path: '/support/knowledge-base/category/question',
          name: 'knowledge-base-category-question',
          component: () => import('@/views/pages/knowledge-base/KnowledgeBaseCategoryQuestion.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'KnowledgeBase', url: '/support/knowledge-base' },
              { title: 'Category', url: '/support/knowledge-base/category' },
              { title: 'Question', active: true }
            ],
            parent: 'knowledge-base',
            rule: 'sales'
          }
        },
        {
          path: '/pages/search',
          name: 'page-search',
          component: () => import('@/views/pages/Search.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'Search', active: true }
            ],
            pageTitle: 'Search',
            rule: 'sales'
          }
        },
        {
          path: '/pages/invoice',
          name: 'page-invoice',
          component: () => import('@/views/pages/Invoice.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'Invoice', active: true }
            ],
            pageTitle: 'Invoice',
            rule: 'sales'
          }
        },
        {
          path: '/billing/history',
          name: 'billing-history',
          component: () => import('@/views/pages/billing/Billings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Billings' },
              { title: 'History', active: true }
            ],
            pageTitle: 'Billing History',
            rule: 'sales'
          }
        },
        {
          path: '/billing/information',
          name: 'billing-information',
          component: () => import('@/views/pages/billing/Billings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Billings' },
              { title: 'Information', active: true }
            ],
            pageTitle: 'Billing Information',
            rule: 'sales'
          }
        },
        {
          path: '/billing/current-plan',
          name: 'billing-current-plan',
          component: () => import('@/views/pages/register/Billings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Billings' },
              { title: 'Current Plan', active: true }
            ],
            pageTitle: 'Current Plan ',
            rule: 'sales'
          }
        },
        {
          path: '/billing/payout-settings',
          name: 'billing-payout-settings',
          component: () => import('@/views/pages/billing/Billings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Billings' },
              { title: 'Payout Settings', active: true }
            ],
            pageTitle: 'Payout Settings',
            rule: 'sales'
          }
        },
        {
          path: '/reports',
          name: 'Reports',
          component: () => import('@/views/pages/reports/reports.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Reports', active: true }
            ],
            pageTitle: 'Reports',
            rule: 'sales'
          }
        }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/callback',
          name: 'auth-callback',
          component: () => import('@/views/Callback.vue'),
          meta: {
            rule: 'sales'
          }
        },
        {
          path: '/login',
          name: 'page-login',
          component: () => import('@/views/pages/login/Login.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/invite/:token',
          name: 'page-invite',
          component: () => import('@/views/pages/Invite.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/logout',
          name: 'page-logout',
          component: () => import('@/views/pages/login/Logout.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/login/organization',
          name: 'page-oganization-list',
          component: () => import('@/views/pages/login/OrganizationList.vue'),
          meta: {
            rule: 'sales'
          }
        },
        {
          path: '/signup',
          name: 'page-signup',
          component: () => import('@/views/pages/register/SignUp.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/signup/verify',
          name: 'page-verify',
          component: () => import('@/views/pages/register/Verification.vue'),
          meta: {
            rule: 'sales'
          }
        },
        {
          path: '/signup/orgdetails',
          name: 'page-orgdetails',
          component: () => import('@/views/pages/register/Organization.vue'),
          meta: {
            rule: 'sales'
          }
        },
        {
          path: '/signup/details',
          name: 'page-details',
          component: () => import('@/views/pages/register/Personal.vue'),
          meta: {
            rule: 'sales'
          }
        },
        {
          path: '/signup/card',
          name: 'page-ccard',
          component: () => import('@/views/pages/register/CreditCard.vue'),
          meta: {
            rule: 'sales'
          }
        },
        {
          path: '/signup/package',
          name: 'page-package',
          component: () => import('@/views/pages/register/Subscription.vue'),
          meta: {
            rule: 'sales'
          }
        },
        {
          path: '/forgot-password',
          name: 'page-forgot-password',
          component: () => import('@/views/pages/ForgotPassword.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/reset/:token',
          name: 'page-reset-password',
          component: () => import('@/views/pages/ResetPassword.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/pages/lock-screen',
          name: 'page-lock-screen',
          component: () => import('@/views/pages/LockScreen.vue'),
          meta: {
            rule: 'sales'
          }
        },
        {
          path: '/pages/comingsoon',
          name: 'page-coming-soon',
          component: () => import('@/views/pages/ComingSoon.vue'),
          meta: {
            rule: 'sales'
          }
        },
        {
          path: '/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/error-500',
          name: 'page-error-500',
          component: () => import('@/views/pages/Error500.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/maintenance',
          name: 'page-maintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
          meta: {
            rule: 'sales'
          }
        }
      ]
    },
    
    //static pages
    {
      path: '/home',
      name: 'HomePage',
      component: () => import('@/views/static/HomePage.vue'),
      meta: {
        rule: 'guest'
      }
    },
    {
      path: '/tnc',
      name: 'TnC',
      component: () => import('@/views/static/TnC.vue'),
      meta: {
        rule: 'guest'
      }
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: () => import('@/views/static/PrivacyPolicy.vue'),
      meta: {
        rule: 'guest'
      }
    },
    {
      path: '/faq',
      name: 'FaQ',
      component: () => import('@/views/static/FaQ.vue'),
      meta: {
        rule: 'guest'
      }
    },
    {
      path: '',
      component: () => import('@/layouts/nav-bar/navbar.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/buy/:formid',
          name: 'buyer-landing',
          component: () => import('@/views/pages/buyer/landing.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/buynow/:branded',
          name: 'buyer-landing',
          component: () => import('@/views/pages/buyer/landing.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/previewbuy/:formid',
          name: 'buyer-landing',
          component: () => import('@/views/pages/buyer/landing.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/buy/checkout',
          name: 'buyer-checkout',
          component: () => import('@/views/pages/buyer/checkout.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/address/:orderid',
          name: 'buyer-address',
          component: () => import('@/views/pages/buyer/address.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/order/:orderid',
          name: 'buyer-landing',
          component: () => import('@/views/pages/buyer/landing.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/summary/:orderid',
          name: 'buyer-summary',
          component: () => import('@/views/pages/buyer/summary.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/summary/complete/:orderid',
          name: 'buyer-summary',
          component: () => import('@/views/pages/buyer/complete.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/buy/products',
          name: 'buyer-product',
          component: () => import('@/views/pages/buyer/products.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/buy/cart',
          name: 'buyer-cart',
          component: () => import('@/views/pages/buyer/cart.vue'),
          meta: {
            rule: 'guest'
          }
        },
        {
          path: '/paymentsuccess/:orderid',
          name: 'buyer-thank-you',
          component: () => import('@/views/pages/buyer/thankYou.vue'),
          meta: {
            rule: 'guest'
          }
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/error-404'
    }
  ]
})



const ONBOARD_PATHS = [
  '/signup',
  '/signup/verify',
  '/signup/details',
  '/signup/orgdetails',
  '/signup/card',
  '/signup/package'
];

router.beforeResolve(async (to, from, next) => {
  let destinationRule = '';

  try {
    destinationRule = to.meta.rule;
  } catch (e) {
    destinationRule = 'guest';
  }

  let loginStatus = auth.getLoginStatus();

  if (to.path === '/login' && loginStatus !== 0) {
    next( { path: '/dashboard' } )
  }

  // if the destination rule is guest, pass without any 
  // other processing
  if (destinationRule === 'guest') {
    next();
    return;
  }


  if (loginStatus === auth.NOT_LOGGED_IN) {
    // force to login when user is not logged in yet
    // this block is required to avoid circular calling
    if (to.path !== '/login') {
      next( {path: '/login'} );
    } else {
      next();
    }
    return;
  }

  if (loginStatus === auth.REQUIRES_RELOAD) {
    await auth.reloadLogin().then((result) => {
      next();
      return;
    }).catch(error => {
      store.commit('RESET_STATE');
      url.rawRedirect('login');
    });
  }
  
  // at this point we are sure that current user is logged in
  // and has some state in our store (loginStatus == auth.LOGGED_IN)
  let onboardStep = await auth.getOnboardStep()

  console.log({
    onboardStep: onboardStep,
    len: ONBOARD_PATHS.length,
    cur: ONBOARD_PATHS[onboardStep],
    path: to.path
  })

  if (onboardStep >= ONBOARD_PATHS.length) {
    next();
  } else {
    if (ONBOARD_PATHS[onboardStep] === to.path) {
      next();
      return;
    } else {
      next({
        path: ONBOARD_PATHS[onboardStep]
      });
      return;
    }
  }
});

router.beforeEach((to, from, next)=>{
  const subdir = window.location.host.split('.')[0];
  var domain = "quicksend.asia"; //live change to quicksend.asia
  var pageToLoad = 'dashboard';

  console.dir({
    to: to,
    from: from
  })

  if(subdir!== domain && to.name!==pageToLoad){
      //next({name:pageToLoad, params:{}})
      console.log(subdir);
      store.commit('SET_SUBDOMAIN',subdir);
      next();
  }
  else{
    store.commit('SET_SUBDOMAIN','');
    next();
  }
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
